import { useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { OWASP_2025_CATEGORIES, normalizeOwaspStatus } from '@/lib/owasp';
import { NAV_LABELS, STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';
import { isKnownControlKey, resolveControlKey } from '@/lib/controlRegistry';

const POLICY_ENDPOINT = 'site-policy';

const statusMeta = (status: string) => {
  switch (status) {
    case 'action':
      return { label: STATUS_LABELS.actionRequired, tone: 'danger' as const, icon: ShieldAlert };
    case 'partial':
      return { label: STATUS_LABELS.needsHost, tone: 'warning' as const, icon: ShieldAlert };
    case 'covered':
      return { label: STATUS_LABELS.covered, tone: 'success' as const, icon: ShieldCheck };
    default:
      return { label: STATUS_LABELS.monitoringPlanned, tone: 'neutral' as const, icon: Shield };
  }
};

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

export default function DomainOverview() {
  const navigate = useNavigate();
  const { domain, managedSite } = useOutletContext<{
    domain: { id: string; domain_name: string };
    managedSite?: { id?: string | null } | null;
  }>();
  const [vulnDialogOpen, setVulnDialogOpen] = useState(false);
  const vulnUiEnabled = import.meta.env.VITE_VULN_ENRICHMENT_ENABLED === 'true';

  const logsQuery = useQuery({
    queryKey: ['domain-owasp-logs', domain.id],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('owasp_id, control_key, status, recommended_action, last_checked_at, created_at')
        .eq('domain_id', domain.id)
        .not('owasp_id', 'is', null)
        .gte('created_at', cutoff)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const securityControlsQuery = useQuery({
    queryKey: ['domain-security-controls', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_logs')
        .select('control_key, status, enforced, evidence, last_checked_at, created_at')
        .eq('domain_id', domain.id)
        .eq('category', 'security_control')
        .in('control_key', [
          'autoupdate_plugins_detect',
          'password_policy_enforced',
          'admin_geo_guard',
          'mfa_coverage_snapshot',
          'geo_policy_detect',
          'mfa_coverage',
        ])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const policyQuery = useQuery({
    queryKey: ['site-policy-overview', managedSite?.id],
    queryFn: async () => {
      if (!managedSite?.id) return null;
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/${POLICY_ENDPOINT}?site_id=${managedSite.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-SS-Auth': 'user',
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load policy');
      }
      return payload;
    },
    enabled: Boolean(managedSite?.id),
  });

  const postureQuery = useQuery({
    queryKey: ['domain-security-posture', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_security_posture')
        .select('coverage_score, posture_score, coverage_breakdown, posture_breakdown, computed_at')
        .eq('domain_id', domain.id)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      if (data) return data;
      const { data: computed, error: computeError } = await supabase.functions.invoke('security-posture', {
        body: { domain_id: domain.id },
      });
      if (computeError) {
        if (computeError.status === 401) {
          navigate('/portal/login', { replace: true });
          throw new Error('Session expired');
        }
        throw computeError;
      }
      return computed;
    },
  });

  const mfaSnapshotQuery = useQuery({
    queryKey: ['domain-mfa-coverage', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_logs')
        .select('metadata, created_at')
        .eq('domain_id', domain.id)
        .eq('event_type', 'mfa_coverage_snapshot')
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
  });

  const vulnSummaryQuery = useQuery({
    queryKey: ['domain-vuln-summary', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_vuln_findings')
        .select('summary_json, findings_json, captured_at, source_meta_json')
        .eq('domain_id', domain.id)
        .order('captured_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data ?? null;
    },
    enabled: vulnUiEnabled,
  });

  const summary = useMemo(() => {
    const logs = logsQuery.data || [];
    const latestByControl = new Map<string, { owasp_id: string; status: string; created_at: string; last_checked_at?: string | null; }>();

    logs.forEach((log) => {
      if (!log.control_key) return;
      const current = latestByControl.get(log.control_key);
      if (!current || log.created_at > current.created_at) {
        latestByControl.set(log.control_key, {
          owasp_id: log.owasp_id,
          status: log.status || 'ok',
          created_at: log.created_at,
          last_checked_at: log.last_checked_at,
        });
      }
    });

    const categoryStatus = new Map<string, string>();
    let lastChecked = '';

    latestByControl.forEach((control) => {
      const normalized = normalizeOwaspStatus(control.status);
      const existing = categoryStatus.get(control.owasp_id);
      if (!existing || (existing !== 'critical' && normalized === 'critical') || (existing === 'ok' && normalized === 'warn')) {
        categoryStatus.set(control.owasp_id, normalized === 'resolved' ? 'ok' : normalized);
      }
      const candidate = control.last_checked_at || control.created_at;
      if (candidate && candidate > lastChecked) lastChecked = candidate;
    });

    let overall = 'planned';
    const statuses = Array.from(categoryStatus.values());
    if (statuses.includes('critical')) overall = 'action';
    else if (statuses.includes('warn')) overall = 'partial';
    else if (statuses.length > 0) overall = 'covered';

    const labelMap: Record<string, string> = {
      A01: 'Account & access protection',
      A02: 'Transport & crypto posture',
      A03: 'Input safety signals',
      A04: 'Security guardrails',
      A05: 'Configuration hygiene',
      A06: 'Updates & integrity',
      A07: 'Authentication hardening',
      A08: 'Integrity checks',
      A09: 'Monitoring readiness',
      A10: 'Request controls',
    };

    const topItems = OWASP_2025_CATEGORIES
      .filter((category) => {
        const status = categoryStatus.get(category.id);
        return status === 'critical' || status === 'warn';
      })
      .slice(0, 3)
      .map((category) => labelMap[category.id] || category.name);

    return {
      overall,
      lastChecked,
      topItems,
      categoryCount: categoryStatus.size,
    };
  }, [logsQuery.data]);

  const meta = statusMeta(summary.overall);
  const MetaIcon = meta.icon;
  const mfaDetails = (mfaSnapshotQuery.data?.metadata as any)?.details ?? {};
  const mfaTotal = Number(mfaDetails.admin_total ?? 0);
  const mfaEnrolled = Number(mfaDetails.admin_enrolled ?? 0);
  const mfaPercent = Number(mfaDetails.coverage_percent ?? 0);
  const mfaProvider = String(mfaDetails.provider ?? '');
  const mfaLastChecked = mfaDetails.last_checked_at ?? mfaSnapshotQuery.data?.created_at ?? null;

  let mfaStatus: { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' } = {
    label: STATUS_LABELS.monitoringPlanned,
    variant: 'neutral',
  };
  if (mfaProvider && mfaTotal > 0) {
    if (mfaPercent >= 90) {
      mfaStatus = { label: STATUS_LABELS.covered, variant: 'success' };
    } else if (mfaPercent >= 50) {
      mfaStatus = { label: STATUS_LABELS.managed, variant: 'warning' };
    } else {
      mfaStatus = { label: STATUS_LABELS.actionRequired, variant: 'danger' };
    }
  } else if (mfaSnapshotQuery.data) {
    mfaStatus = { label: STATUS_LABELS.managed, variant: 'warning' };
  }

  const latestByControl = useMemo(() => {
    const map = new Map<string, any>();
    const unknown = new Set<string>();
    (securityControlsQuery.data || []).forEach((entry: any) => {
      if (!entry.control_key) return;
      const normalized = resolveControlKey(entry.control_key);
      if (!isKnownControlKey(normalized)) {
        unknown.add(entry.control_key);
        return;
      }
      if (!map.has(normalized)) {
        map.set(normalized, { ...entry, control_key: normalized });
      }
    });
    if (import.meta.env.DEV && unknown.size > 0) {
      console.warn('Unknown security control keys detected', Array.from(unknown));
    }
    return map;
  }, [securityControlsQuery.data]);

  const autoUpdate = latestByControl.get('autoupdate_plugins_detect');
  const autoUpdateEvidence = autoUpdate?.evidence ?? {};
  const policyControls = policyQuery.data?.policy?.security_controls ?? {};
  const desiredAutoupdate = policyControls?.autoupdate ?? {};
  const desiredAutoEnabled = Boolean(desiredAutoupdate.enabled);
  const desiredAutoMode = desiredAutoupdate.mode === 'all' ? 'all' : 'allowlist';
  const desiredCoreAutoEnabled = Boolean(desiredAutoupdate.core_enabled);
  const autoUpdateLabel = desiredAutoEnabled ? 'On' : 'Off';
  const autoUpdateMode = desiredAutoMode;
  const telemetryAutoEnabled =
    typeof autoUpdateEvidence.enabled === 'boolean'
      ? autoUpdateEvidence.enabled
      : Boolean(autoUpdateEvidence.plugins_desired_enabled);
  const telemetryAutoMode =
    typeof autoUpdateEvidence.mode === 'string'
      ? autoUpdateEvidence.mode
      : (typeof autoUpdateEvidence.plugins_desired_mode === 'string' ? autoUpdateEvidence.plugins_desired_mode : null);
  const autoUpdateDrift = autoUpdateEvidence.plugins_drift === true;
  const coreAutoLabel = desiredCoreAutoEnabled ? 'On' : 'Off';
  const coreAutoDrift = autoUpdateEvidence.core_drift === true;
  const coreAppliedState = typeof autoUpdateEvidence.core_applied_state === 'string'
    ? autoUpdateEvidence.core_applied_state
    : null;

  const desiredPassword = policyControls?.password_policy ?? {};
  const passwordLabel = desiredPassword.enforce_min_length || desiredPassword.enforce_breached
    ? 'Enforced on change/reset'
    : 'Not enforced';
  const passwordMin = typeof desiredPassword.min_length === 'number' ? desiredPassword.min_length : 12;
  const breachBlocking = desiredPassword.enforce_breached ? 'Enabled' : 'Disabled';

  const adminGeo = latestByControl.get('admin_geo_guard');
  const adminGeoEvidence = adminGeo?.evidence ?? {};
  const desiredAdminGeoPolicy = policyControls?.admin_geo_guard ?? {};
  const telemetryAdminGeoPolicy = adminGeoEvidence.policy ?? {};
  const adminGeoPolicy = Object.keys(desiredAdminGeoPolicy).length > 0 ? desiredAdminGeoPolicy : telemetryAdminGeoPolicy;
  const adminGeoMode = adminGeoPolicy.mode ? String(adminGeoPolicy.mode) : 'observe';
  const adminGeoAllowCount = Array.isArray(adminGeoPolicy.allow_countries) ? adminGeoPolicy.allow_countries.length : 0;
  const adminGeoDenyCount = Array.isArray(adminGeoPolicy.deny_countries) ? adminGeoPolicy.deny_countries.length : 0;
  const adminGeoConfidenceLevel = typeof adminGeoEvidence?.confidence?.level === 'string'
    ? adminGeoEvidence.confidence.level
    : null;

  const vulnSummary = (vulnSummaryQuery.data?.summary_json as any) ?? null;
  const vulnFindings = (vulnSummaryQuery.data?.findings_json as any[]) ?? [];
  const vulnCapturedAt = vulnSummaryQuery.data?.captured_at ?? null;
  const vulnTotal = typeof vulnSummary?.total === 'number' ? vulnSummary.total : 0;
  const vulnBySeverity = vulnSummary?.by_severity ?? {};
  const hasVulnFindings = Array.isArray(vulnFindings) && vulnFindings.length > 0;
  const hasVulnReport = Boolean(vulnCapturedAt);

  return (
    <div className="grid gap-6">
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Coverage & posture</CardTitle>
          <CardDescription>Two explainable scores based on live signals.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div>
              <div className="text-foreground font-semibold">Coverage</div>
              <div>{postureQuery.data?.coverage_score ?? 0}/100</div>
            </div>
            <div>
              <div className="text-foreground font-semibold">Posture</div>
              <div>{postureQuery.data?.posture_score ?? 0}/100</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Computed {postureQuery.data?.computed_at ? formatTimestamp(postureQuery.data?.computed_at) : 'Unknown'}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MetaIcon className="h-5 w-5" />
            Security Health
          </CardTitle>
          <CardDescription>Based on protection coverage signals and the last 30 days of activity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant={meta.tone}>{meta.label}</Badge>
            <span className="text-sm text-muted-foreground">Coverage checks reported: {summary.categoryCount}/10</span>
          </div>
          <div className="text-sm text-muted-foreground">Last coverage check: {formatTimestamp(summary.lastChecked)}</div>
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
            <div className="font-semibold">Top items</div>
            {summary.topItems.length === 0 ? (
              <div className="text-muted-foreground">No active coverage gaps detected.</div>
            ) : (
              <ul className="list-disc list-inside text-muted-foreground">
                {summary.topItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          <Button variant="outline" onClick={() => navigate(`/portal/domains/${domain.id}/coverage`)}>
            View {NAV_LABELS.protectionCoverage}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>MFA coverage</CardTitle>
          <CardDescription>Admin MFA enrollment status reported by the connector.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge variant={mfaStatus.variant}>{mfaStatus.label}</Badge>
            <span className="text-sm text-muted-foreground">
              Admins with MFA: {mfaTotal > 0 ? `${mfaEnrolled} / ${mfaTotal}` : 'Unknown'}
              {mfaTotal > 0 ? ` (${mfaPercent.toFixed(1)}%)` : ''}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">Provider: {mfaProvider || 'Unknown'}</div>
          <div className="text-sm text-muted-foreground">Last checked: {formatTimestamp(mfaLastChecked)}</div>
        </CardContent>
      </Card>

      {vulnUiEnabled && (
        <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Known vulnerabilities</CardTitle>
          <CardDescription>Read-only intelligence matched against installed components.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={!hasVulnReport ? 'neutral' : vulnTotal === 0 ? 'success' : 'warning'}>
              {!hasVulnReport ? 'Monitoring planned' : vulnTotal === 0 ? 'No known issues' : `${vulnTotal} findings`}
            </Badge>
            <span>Last analyzed: {formatTimestamp(vulnCapturedAt)}</span>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <Badge variant="outline">Critical: {vulnBySeverity.critical ?? 0}</Badge>
            <Badge variant="outline">High: {vulnBySeverity.high ?? 0}</Badge>
            <Badge variant="outline">Medium: {vulnBySeverity.medium ?? 0}</Badge>
            <Badge variant="outline">Low: {vulnBySeverity.low ?? 0}</Badge>
          </div>
          <Dialog open={vulnDialogOpen} onOpenChange={setVulnDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!hasVulnFindings}>
                View details
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Known vulnerabilities</DialogTitle>
                <DialogDescription>Minimal indicators with references. Full narratives are not shown.</DialogDescription>
              </DialogHeader>
              {!hasVulnFindings ? (
                <p className="text-sm text-muted-foreground">No vulnerability findings reported yet.</p>
              ) : (
                <div className="space-y-3">
                  {vulnFindings.map((finding, index) => {
                    const references = Array.isArray(finding?.references) ? finding.references : [];
                    return (
                      <div key={`${finding?.vuln_id ?? 'finding'}-${index}`} className="rounded-md border border-border/60 p-3">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <Badge variant="secondary">{finding?.severity ?? 'unknown'}</Badge>
                          <Badge variant={finding?.confidence === 'multi-source' ? 'success' : 'outline'}>
                            {finding?.confidence === 'multi-source' ? 'Verified by multiple sources' : 'Single-source'}
                          </Badge>
                          <span className="text-foreground font-medium">
                            {finding?.component_type} · {finding?.slug}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          Installed: {finding?.installed_version ?? '—'} · Patched: {finding?.patched ? 'Yes' : 'No'}
                        </div>
                        {finding?.remediation && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Remediation: {finding.remediation}
                          </div>
                        )}
                        {references.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-2 space-y-1">
                            {references.slice(0, 3).map((ref: string) => (
                              <a key={ref} className="text-primary underline block" href={ref} target="_blank" rel="noreferrer">
                                {ref}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      )}

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Additional security controls</CardTitle>
          <CardDescription>Signals based on plugin configuration and policy.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-center justify-between gap-3">
            <span>Plugin auto-updates</span>
            <span className="text-foreground text-right">
              {autoUpdateLabel} · Mode: {autoUpdateMode === 'all' ? 'All' : 'Allowlist'} · Themes excluded
              {autoUpdateDrift ? ' · Drift detected' : ''}
              {telemetryAutoMode ? ` · Telemetry: ${telemetryAutoEnabled ? 'On' : 'Off'} (${telemetryAutoMode === 'all' ? 'All' : 'Allowlist'})` : ''}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>WordPress core auto-updates</span>
            <span className="text-foreground text-right">
              {coreAutoLabel}
              {coreAutoDrift ? ' · Drift detected' : ''}
              {coreAppliedState ? ` · Applied: ${coreAppliedState}` : ''}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Password policy (change/reset)</span>
            <span className="text-foreground">
              {passwordLabel} · Min {passwordMin} (admins) · Breached blocking: {breachBlocking}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Admin access location rules</span>
            <span className="text-foreground text-right">
              {adminGeoMode === 'enforce' ? 'Enforce' : 'Observe'} · Allowlist: {adminGeoAllowCount} · Denylist: {adminGeoDenyCount}
              {adminGeoConfidenceLevel ? ` · Confidence: ${adminGeoConfidenceLevel}` : ''}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
